import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import XLR8Logo from './XLR8Logo'
import LoadError from './LoadError'
import { getCustomers } from '../api'
import { loadList } from '../utils/loadState'
import { useSyncStatus } from '../contexts/SyncStatusContext'
import { useAuth } from '../AuthProvider'
import './TopNav.css'

const PRODUCT_LABELS = {
  ukg_pro: 'UKG Pro',
  ukg_pro_wfm: 'UKG WFM',
  ukg_pro_soap: 'UKG Pro',
}
function formatProduct(pid) {
  if (!pid) return 'Sync'
  if (PRODUCT_LABELS[pid]) return PRODUCT_LABELS[pid]
  return pid.split('_').map(w => w.toUpperCase() === 'UKG' ? 'UKG' : (w.charAt(0).toUpperCase() + w.slice(1))).join(' ')
}

export default function TopNav({ breadcrumbs = [], actions }) {
  const location = useLocation()
  const navigate = useNavigate()
  const path = location.pathname
  const [showWorkEffortMenu, setShowWorkEffortMenu] = useState(false)
  // null = not loaded yet. A failed load is an error with a Retry -- never an endless
  // "Loading…" and never an empty menu (item 201).
  const [customers, setCustomers] = useState(null)
  const [customersError, setCustomersError] = useState(null)
  const menuRef = useRef(null)
  const { job: syncJob, customerId: syncCustomerId, isComplete: syncComplete } = useSyncStatus()
  // R-B: the Admin link is shown to admins only, and the main app has a Sign Out.
  const { isAdmin: userIsAdmin, profile, signOut } = useAuth()
  const initials = ((profile?.display_name || profile?.email || '?')
    .split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')) || '?'

  async function handleSignOut() {
    await signOut()
    navigate('/', { replace: true })
  }
  // SYS-01: a partial sync must not flash the same green "Sync complete" as a clean one.
  const syncErrors = syncComplete && syncJob?.status === 'completed_with_errors'

  const isHome = path === '/home'
  const isWorkEffort = path.includes('/work-efforts')
  const isCustomers = path.startsWith('/customers') && !isWorkEffort
  const isThreads = path.startsWith('/threads')
  const isExports = path.startsWith('/exports')
  const isAdmin = path.startsWith('/admin')

  async function loadMenuCustomers() {
    setCustomersError(null)
    const { items, error } = await loadList(getCustomers, 'contexts', 'customers')
    setCustomers(items)
    setCustomersError(error)
  }

  useEffect(() => {
    if (showWorkEffortMenu && customers === null) loadMenuCustomers()
  }, [showWorkEffortMenu])

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowWorkEffortMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleSelectCustomer(customerId) {
    setShowWorkEffortMenu(false)
    navigate(`/customers/${customerId}/work-efforts/new`)
  }

  return (
    <nav className="top-nav">
      <div className="nav-left">
        <Link to="/home" className="logo-group">
          <img src="/sempirical-lockup.svg" alt="Sempirical" className="nav-wordmark" />
        </Link>
        <div className="nav-links">
          <Link to="/home" className={`nav-pill${isHome ? ' active' : ''}`}>Home</Link>
          <Link to="/customers" className={`nav-pill${isCustomers ? ' active' : ''}`}>Customers</Link>
          <div className="nav-pill-dropdown" ref={menuRef}>
            <button
              className={`nav-pill nav-pill-btn${isWorkEffort ? ' active' : ''}`}
              onClick={() => setShowWorkEffortMenu(v => !v)}
            >
              Work Effort
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{marginLeft:4,opacity:0.6}}>
                <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </button>
            {showWorkEffortMenu && (
              <div className="nav-dropdown-menu">
                <div className="nav-dropdown-label">Select a customer</div>
                {customersError ? (
                  <LoadError inline what="customers" error={customersError} onRetry={loadMenuCustomers} />
                ) : customers === null ? (
                  <div className="nav-dropdown-loading">Loading…</div>
                ) : customers.length === 0 ? (
                  <div className="nav-dropdown-loading">No customers</div>
                ) : (
                  customers.map(c => (
                    <button
                      key={c.customer_id}
                      className="nav-dropdown-item"
                      onClick={() => handleSelectCustomer(c.customer_id)}
                    >
                      <span className="nav-dropdown-name">{c.company_name}</span>
                      <span className="nav-dropdown-meta">{c.hcm_product || c.hcm_vendor || ''}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Link to="/threads" className={`nav-pill${isThreads ? ' active' : ''}`}>Thread Library</Link>
          <Link to="/exports" className={`nav-pill${isExports ? ' active' : ''}`}>Export Center</Link>
        </div>
      </div>
      <div className="nav-right">
        {/* Breadcrumbs (shown after nav links when provided) */}
        {breadcrumbs.length > 0 && (
          <div className="breadcrumb">
            {breadcrumbs.map((crumb, i) => (
              <span key={i}>
                {i > 0 && <span className="sep">›</span>}
                {crumb.href ? (
                  <Link to={crumb.href}>{crumb.label}</Link>
                ) : (
                  <span className="current">{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        )}
        {userIsAdmin && (
          <Link to="/admin" className={`nav-pill admin-link${isAdmin ? ' active' : ''}`}>Admin</Link>
        )}
        <div className="nav-divider" />
        <div className="nav-icon-btn">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
        </div>
        <div className="nav-icon-btn">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><circle cx="12" cy="12" r="3"/></svg>
        </div>
        {actions && <>{actions}</>}
        {syncJob && (
          <button
            className={`nav-sync-pill${syncComplete ? (syncErrors ? ' complete-errors' : ' complete') : ''}`}
            onClick={() => syncCustomerId && navigate(`/customers/${syncCustomerId}/data`)}
            title={syncJob.current_step || (syncComplete ? (syncErrors ? 'Sync completed with errors' : 'Sync complete') : 'Syncing…')}
          >
            <span className="nav-sync-dot" />
            <span className="nav-sync-text">
              <span className="nav-sync-label">
                {syncComplete ? (syncErrors ? 'Completed with errors' : 'Sync complete') : `Syncing ${formatProduct(syncJob.product_id)}`}
              </span>
              {!syncComplete && (
                <span className="nav-sync-meta">
                  {syncJob.tables_processed}/{syncJob.total_tables} tables · {(syncJob.total_rows || 0).toLocaleString()} rows
                </span>
              )}
            </span>
            {!syncComplete && syncJob.total_tables > 0 && (
              <span className="nav-sync-bar">
                <span
                  className="nav-sync-bar-fill"
                  style={{ width: `${Math.min(100, Math.round((100 * (syncJob.tables_processed || 0)) / syncJob.total_tables))}%` }}
                />
              </span>
            )}
          </button>
        )}
        <div className="user-avatar" title={profile?.email || ''}>{initials}</div>
        <button className="nav-btn" onClick={handleSignOut}>Sign Out</button>
      </div>
    </nav>
  )
}

